import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppLayout } from 'src/components/layout/app-layout';
import { useNewCampaignRequestCount } from 'src/features/manage-clients/campaigns/hooks/use-new-campaign-request-count';
import 'src/features/manage-clients/campaigns/endpoints/launched-campaigns.endpoints';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import CampaignEmailsManageTab from './tabs/campaign-emails.tab';
import ManageAccountsCampaignsTab from './tabs/manage-accounts-campaigns.tab';
import ManageClientsTab from './tabs/manage-clients.tab';
import ManageLaunchedCampaignsTab from './tabs/manage-launched-campaigns.tab';
import ManageUsersTab from './tabs/manage-users.tab';
import SettingsTab from './tabs/settings.tab';

const CLIENT_MANAGEMENT_TABS = [
  {
    id: '',
    key: 'clients',
    label: 'Clients',
  },
  {
    id: 'accounts-campaigns',
    key: 'accounts-campaigns',
    label: 'Accounts & Campaigns',
  },
  {
    id: 'campaign-emails',
    key: 'campaign-emails',
    label: 'Campaign Emails',
  },
  {
    id: 'settings',
    key: 'settings',
    label: 'Settings',
  },
  {
    id: 'users',
    key: 'users',
    label: 'Users',
  },
  {
    id: 'campaigns',
    key: 'launched-campaigns',
    label: 'Requests',
  },
] as const;

const LEGACY_ACCOUNT_CAMPAIGN_TABS = new Set(['manage-accounts', 'manage-campaigns']);

const ClientManagementPage = () => {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const [searchParams] = useSearchParams();
  const newRequestCount = useNewCampaignRequestCount();

  useEffect(() => {
    if (!tab || !LEGACY_ACCOUNT_CAMPAIGN_TABS.has(tab)) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'manage-campaigns' && nextParams.get('view') !== 'campaigns') {
      nextParams.set('view', 'campaigns');
    }
    const qs = nextParams.toString();
    navigate(`/manage-clients/accounts-campaigns${qs ? `?${qs}` : ''}`, { replace: true });
  }, [navigate, searchParams, tab]);

  const clientManagementTabs = useMemo(
    () => CLIENT_MANAGEMENT_TABS.map((clientTab) => {
      if (clientTab.id === 'campaigns' && newRequestCount > 0) {
        return {
          ...clientTab,
          badge: 1,
          badgeVariant: 'dot' as const,
        };
      }
      return clientTab;
    }),
    [newRequestCount]
  );

  const validTabs = clientManagementTabs.map((clientTab) => clientTab.id);
  type TabType = (typeof validTabs)[number];

  const currentTab: TabType = validTabs.includes(tab as TabType)
    ? (tab as TabType)
    : '';

  const pageTitle =
    clientManagementTabs.find((clientTab) => clientTab.id === currentTab)?.label ?? 'Clients';
  useSetPageTitle(pageTitle);

  const handleTabChange = (tabId: string) => {
    const qs = searchParams.toString();
    navigate(`/manage-clients/${tabId}${qs ? `?${qs}` : ''}`);
  };

  if (tab && LEGACY_ACCOUNT_CAMPAIGN_TABS.has(tab)) {
    return (
      <AppLayout
        activePage="/client-management"
        navbarProps={{
          tabs: clientManagementTabs,
          activeTab: 'accounts-campaigns',
          onTabChange: handleTabChange,
        }}
      >
        <div className="h-[calc(100vh-8rem)]" />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      activePage="/client-management"
      navbarProps={{
        tabs: clientManagementTabs,
        activeTab: currentTab,
        onTabChange: handleTabChange,
      }}
    >
      <div className="h-[calc(100vh-8rem)]">
        {currentTab === '' && <ManageClientsTab />}
        {currentTab === 'accounts-campaigns' && <ManageAccountsCampaignsTab />}
        {currentTab === 'campaign-emails' && <CampaignEmailsManageTab />}
        {currentTab === 'settings' && <SettingsTab />}
        {currentTab === 'users' && <ManageUsersTab />}
        {currentTab === 'campaigns' && <ManageLaunchedCampaignsTab />}
      </div>
    </AppLayout>
  );
};

export default ClientManagementPage;
